import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { SeparationListWithItems, SeparationStatus } from '../types'

export interface LinkedFilial {
  filial_company_id: string
  filial_name: string
}

interface NewItem {
  sku: string
  product_name: string
  quantity: number
  priority: string | null
  current_stock: number | null
  matriz_stock: number | null
}

export function useSeparation() {
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null)
  const [filiais, setFiliais] = useState<LinkedFilial[]>([])
  const [lists, setLists] = useState<SeparationListWithItems[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLists = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('separation_lists')
      .select('*, separation_list_items(*)')
      .order('created_at', { ascending: false })
    if (err) { setError(err.message); return }
    setLists((data ?? []) as SeparationListWithItems[])
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoading(false); return }
      const [{ data: profile }, filiaisRes] = await Promise.all([
        supabase.from('profiles').select('company_id').eq('id', user.id).single(),
        supabase.rpc('list_linked_filiais'),
      ])
      setActiveCompanyId((profile?.company_id as string | undefined) ?? null)
      setFiliais((filiaisRes.data ?? []) as LinkedFilial[])
      await fetchLists()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [fetchLists])

  useEffect(() => { fetchAll() }, [fetchAll])

  /** Cria a lista (a empresa ativa é a matriz) + itens. Retorna a lista criada ou erro. */
  const createList = useCallback(async (opts: {
    filialCompanyId: string
    items: NewItem[]
    notes?: string | null
  }): Promise<{ id: string } | { error: string }> => {
    if (!activeCompanyId) return { error: 'Empresa ativa não carregada' }
    if (opts.items.length === 0) return { error: 'Nenhum item selecionado' }

    const { data: { user } } = await supabase.auth.getUser()
    const { data: list, error: listErr } = await supabase
      .from('separation_lists')
      .insert({
        matriz_company_id: activeCompanyId,
        filial_company_id: opts.filialCompanyId,
        status: 'criada',
        notes: opts.notes ?? null,
        created_by: user?.id ?? null,
      })
      .select()
      .single()
    if (listErr || !list) return { error: listErr?.message ?? 'Erro ao criar lista' }

    const itemsInsert = opts.items.map(i => ({
      separation_list_id: list.id as string,
      sku: i.sku,
      product_name: i.product_name,
      quantity: i.quantity,
      priority: i.priority,
      current_stock: i.current_stock,
      matriz_stock: i.matriz_stock,
    }))
    const { error: itemsErr } = await supabase.from('separation_list_items').insert(itemsInsert)
    if (itemsErr) return { error: itemsErr.message }

    await fetchLists()
    return { id: list.id as string }
  }, [activeCompanyId, fetchLists])

  const updateStatus = useCallback(async (listId: string, status: SeparationStatus): Promise<string | null> => {
    const { error: err } = await supabase
      .from('separation_lists').update({ status }).eq('id', listId)
    if (err) return err.message
    await fetchLists()
    return null
  }, [fetchLists])

  /** Exclui a lista (só a matriz; RLS bloqueia a filial). Remove itens depois a lista. */
  const deleteList = useCallback(async (listId: string): Promise<string | null> => {
    const { error: itemsErr } = await supabase
      .from('separation_list_items').delete().eq('separation_list_id', listId)
    if (itemsErr) return itemsErr.message
    const { error: listErr } = await supabase
      .from('separation_lists').delete().eq('id', listId)
    if (listErr) return listErr.message
    await fetchLists()
    return null
  }, [fetchLists])

  const isMatriz = filiais.length > 0

  return { activeCompanyId, filiais, isMatriz, lists, loading, error, refresh: fetchAll, createList, updateStatus, deleteList }
}
